import React, { useState } from 'react';
import ReactTable from 'react-table';
import { isArray, uniq } from 'lodash';
import 'react-table/react-table.css';
import { ButtonGroup, Form, Popover, OverlayTrigger, Button } from '../components/ui-kit';
import { IconFilter } from '../components/ui-kit/icons';
import './react-table-override.scss';
import GeneralDateTimePicker from '../components/general/DateTimePicker';
import moment from 'moment';

const defaultFilterMethod = (filter, row) => String(row[filter.id]).toLowerCase().includes(filter.value.toLowerCase());

const customThemeOverrides = {
  input: {
    padding: '7px 14px',
    width: '80px',
    paddingRight: 0,
    maxHeight: '21px'
  }
};

const DefaultReactTable = props => {
  let { data, columns, selectable, onSelectChange, selected, ...cleanProps } = props;

  const [pageSizeOverride, setPageSizeOverride] = useState(null);

  // If there is an override value, use it, otherwise use the dynamic
  // size. For visuals, we put a lower (5) and upper (100) bound on
  // the dynamic table size.
  let pageSize = data?.length || 5;
  // do not set lower than 5. Otherwise, the "no results found" message will
  // overlap with the header
  pageSize = pageSizeOverride || Math.min(Math.max(5, pageSize), 100);

  // Finally, we create the list of selectable values, this is mostly
  // react table's defaults, but with the addition of the dynamic size.
  const pageSizeOptions = uniq([pageSize, 5, 10, 20, 25, 50, 100]).sort((a, b) => a - b);

  if (selectable) {
    data = data.map(p => ({ selected: selected.includes(p.id), ...p }));
    const allSelected = data.every(p => p.selected);
    const allIds = data.map(p => p.id);
    const selectAll = () => onSelectChange(allIds, !allSelected);

    const selectableColumn = {
      id: 'selected',
      accessor: d => d.selected,
      sortable: false,
      filterable: false,
      Header: <Form.Check className="text-center" checked={allSelected} onChange={selectAll} />,
      Cell: row => (
        <Form.Check
          className="text-center"
          checked={row.original.selected}
          onChange={() => onSelectChange([row.original.id], !row.original.selected)}
        />
      )
    };

    columns = [selectableColumn, ...columns];
  }

  // when popovers or other server-side components are rendered, we need to
  // rerender them after the table is updated. This is because
  // ReactTable does not trigger a full rerender when the data is updated,
  // so we need to manually trigger the rerender of server-side components
  // after the table is updated. This is specifically needed for the
  // GeneralReports, which can render components on the server
  const rerenderServerSizeComponents = () => {
    setTimeout(() => {
      globalThis.instantiatePopovers();
      globalThis.ReactRailsUJS?.mountComponents();
    }, 500);
  };

  return (
    <ReactTable
      previousText={I18n.t('previous')}
      nextText={I18n.t('next')}
      loadingText={I18n.t('loading')}
      noDataText={I18n.t('no_results_found')}
      pageText={I18n.t('page')}
      ofText={I18n.t('of')}
      rowsText={I18n.t('items')}
      onPageChange={rerenderServerSizeComponents}
      pageSizeOptions={pageSizeOptions}
      pageSize={pageSize}
      onPageSizeChange={pageSize => {
        setPageSizeOverride(pageSize);
        rerenderServerSizeComponents();
      }}
      defaultFilterMethod={defaultFilterMethod}
      columns={columns}
      data={data}
      {...cleanProps}
      onFetchData={props.onFetchDataHandler}
      className={`-striped -highlight ${props.className}`}
      loading={props.loading}
    />
  );
};

// Component for creating a popup in a row header for filtering multiple
// options. A popup is rendered with a checkbox for every option. Options are
// passed as an array of object with { value, html } keys. The value is a simple
// string, the html is how it should be rendered. See multiSelectFilterColumn for
// actually using this.
const MultiSelectFilter = props => {
  const update = (key, checked) => {
    if (props.multi) {
      if (checked) {
        props.onChange([...props.value, key]);
      } else {
        props.onChange(props.value.filter(e => e !== key));
      }
    } else {
      props.onChange(checked ? [key] : []);
    }
  };

  const selectAll = () => props.onChange(props.selectOptions.map(o => o.value));
  const selectNone = () => props.onChange([]);

  const popover = (
    <Popover id="multi-select-filter">
      <Popover.Body>
        {props.multi && (
          <ButtonGroup className="mb-2">
            <Button size="sm" onClick={selectAll} variant="secondary">
              {I18n.t('all')}
            </Button>
            <Button size="sm" onClick={selectNone} variant="secondary">
              {I18n.t('none')}
            </Button>
          </ButtonGroup>
        )}

        {props.selectOptions.map(({ value, html }) => (
          <Form.Check
            key={value}
            checked={props.value.includes(value)}
            onChange={e => update(value, e.target.checked)}
            label={html}
          />
        ))}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger="click" placement="bottom" overlay={popover}>
      <Button variant="outline-secondary">
        <IconFilter />
      </Button>
    </OverlayTrigger>
  );
};

// Utility for quickly creating a row with a filter for multiple discrete
// options. This is useful for filtering a row with enum values for example.
//
// The header of the row shows a filter icon. When clicked, a popup is displayed
// where the user can select/unselect certain values from being shown
export const multiSelectFilterColumn = (
  { show = true, includeEmpty, ...columnOptions },
  multi = true,
  returnAllIfEmpty = false
) => {
  const { selectOptions } = columnOptions;
  const fieldId = columnOptions.id;
  if (!selectOptions) throw new Error('Error: selectOptions must be passed to multiSelectFilterColumn');

  for (const o of selectOptions) {
    // We specifically want to know if the value is undefined or null, not just falsy because we
    // might have a false values.
    if (o.value === undefined || o.value === null)
      throw new Error(`Error: each selectOption must have a 'value' attr ${JSON.stringify(o)}`);
    if (!o.html) throw new Error(`Error: each selectOption must have a 'html' attr ${JSON.stringify(o)}`);
  }

  delete columnOptions.selectOptions; // don't pass this to ReactTable

  const emptyOption = { value: null, html: <i>{I18n.t('none')}</i> };
  const selectOptionsWithEmpty = includeEmpty ? [...selectOptions, emptyOption] : selectOptions;

  return {
    ...columnOptions,
    filterMethod: (filter, row) => {
      // filter.value is the array of selected options
      // row is the row object for which we need to determine whether to show it
      if (!filter || !filter.value) return returnAllIfEmpty;
      // check whether the attribute associated with the current fieldId is
      // included in the filter
      const fieldValue = row[fieldId];
      // The supplied value can be either an array or a string
      if (isArray(fieldValue)) {
        return (
          // if the field value is an array, we want to check whether any of the
          // values in the array are included in the filter value
          fieldValue.some(value => filter.value.includes(value)) ||
          // if the field value is an empty array, we want to check whether the
          // filter value includes null, which is the value we use for "none"
          (fieldValue.length === 0 && filter.value.includes(null))
        );
      }

      return filter.value.includes(fieldValue);
    },
    Filter: ({ filter, onChange }) =>
      show && (
        <MultiSelectFilter
          onChange={onChange}
          value={(filter || {}).value || []}
          selectOptions={selectOptionsWithEmpty}
          fieldId={fieldId}
          multi={multi}
        />
      )
  };
};

export const timeColumn = ({ showDatePicker = true, dateOnly = false, ...options }) => ({
  ...options,
  filterMethod: (filter, row) => {
    if (!filter || !filter.value) return true;

    if (options.id === 'start_time') {
      // show all items that end strictly after the selected start time
      return moment(row.end_time).isAfter(filter.value);
    } else if (options.id === 'end_time') {
      // show all items that start strictly before the selected end time
      return moment(row.start_time).isBefore(filter.value);
    } else {
      throw new Error(`id must be start_time or end_time, but was ${options.id}`);
    }
  },
  width: showDatePicker ? 300 : undefined,
  Filter: ({ filter, onChange }) =>
    showDatePicker && (
      <GeneralDateTimePicker
        onChange={onChange}
        dateOnly={dateOnly || false}
        value={(filter || {}).value}
        showClearButton={true}
        customLabel={null}
        name={options.id === 'start_time' ? 'startTime' : 'endTime'}
        customThemeOverrides={customThemeOverrides}
      />
    )
});

export default DefaultReactTable;
